import { FC, useMemo } from "react";
import { Paper, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { formatCompactNumber } from "../../../../../../../common/utils";
import { ChartNoDataOverlay } from "../../../../../../../components/charts/components/ChartNoDataOverlay";
import { GraphBarChartLegend } from "../../../../../../../components/charts/components/GraphBarChartLegend";
import { MeasurableValueDTO } from "../../../../../../../datasources/api-client/data-contracts";
import { getTransportTypeIcon } from "../../AggregateScenario/data";

export type ModalShiftChartItem = {
	haulageId: number;
	haulageLabel: string;
	baseTkm: MeasurableValueDTO;
	compTkm: MeasurableValueDTO;
	co2Delta: MeasurableValueDTO;
};

type Props = {
	data: ModalShiftChartItem[];
	title?: string;
	baselineLabel?: string;
	comparisonLabel?: string;
	measure?: string;
	numberOfTicks?: number;
	formatTick?: (value: number) => string;
	isLoading?: boolean;
};

const axisWidth = 52;
const chartPaddingX = 2;
const chartPaddingPx = chartPaddingX * 8;
const barHeight = 12;
const barGap = 5;

const ModalShiftBarChart: FC<Props> = ({
	data,
	title,
	baselineLabel = "Baseline",
	comparisonLabel = "Comparison",
	measure = "tkm",
	numberOfTicks = 5,
	formatTick = (value) => formatCompactNumber(value, 0, 1),
	isLoading,
}) => {
	// Find the largest value across both series
	const maxValue = useMemo(
		() => Math.max(...data.flatMap((item) => [item.baseTkm.value, item.compTkm.value]), 0),
		[data]
	);

	// Keep a safe axis even when data is empty
	const axisMaxValue = useMemo(() => Math.max(maxValue, 1), [maxValue]);

	// Create evenly spaced axis ticks
	const ticks = useMemo(
		() =>
			Array.from({ length: numberOfTicks }, (_, index) => {
				if (numberOfTicks === 1) {
					return 0;
				}

				return (index * axisMaxValue) / (numberOfTicks - 1);
			}),
		[axisMaxValue, numberOfTicks]
	);

	const hasNoData = data?.length === 0;

	if (isLoading) {
		return <Skeleton variant="rounded" height={300} width="100%" />;
	}

	return (
		<Paper elevation={0} sx={{ p: 2 }}>
			<Stack spacing={2} width="100%" minHeight={300}>
				{title && (
					<Typography variant="h6" fontWeight={500}>
						{title}
					</Typography>
				)}

				<Stack spacing={2} flex={1} justifyContent="center" position="relative">
					{/* Draw vertical grid lines */}
					<Stack
						position="absolute"
						top={0}
						bottom={0}
						left={`calc(${axisWidth}px + ${chartPaddingPx}px)`}
						right={`${chartPaddingPx}px`}
					>
						{ticks.map((tick, index) => {
							const left = `${(tick / axisMaxValue) * 100}%`;

							return (
								<Stack
									key={`grid-${tick}-${index}`}
									position="absolute"
									left={left}
									top={0}
									bottom={0}
									width="1px"
									bgcolor="divider"
									sx={{
										transform: "translateX(-50%)",
										opacity: 0.75,
									}}
								/>
							);
						})}
					</Stack>

					{hasNoData && <ChartNoDataOverlay />}

					{!hasNoData &&
						data.map((item) => {
							const baseWidth = `${(item.baseTkm.value / axisMaxValue) * 100}%`;
							const compWidth = `${(item.compTkm.value / axisMaxValue) * 100}%`;

							return (
								<Stack
									key={item.haulageId}
									direction="row"
									alignItems="center"
									spacing={1}
									width="100%"
									position="relative"
								>
									{/* Y axis icon and label */}
									<Stack width={axisWidth} alignItems="center" spacing={0.3}>
										{getTransportTypeIcon(item.haulageId)}

										<Typography
											variant="caption"
											color="text.secondary"
											textAlign={"center"}
											lineHeight={1}
											pt={0.25}
										>
											{item.haulageLabel}
										</Typography>
									</Stack>

									{/* Chart row */}
									<Stack flex={1} px={chartPaddingX}>
										<Stack position="relative" height={barHeight * 2 + barGap}>
											{/* Base value */}
											<Tooltip
												title={`${baselineLabel}: ${formatTick(item.baseTkm.value)} ${item.baseTkm.measure}`}
												arrow
											>
												<Stack
													position="absolute"
													left={0}
													top={0}
													width={baseWidth}
													height={barHeight}
													borderRadius="0 4px 4px 0"
													bgcolor="primary.main"
												/>
											</Tooltip>

											{/* Comparison value */}
											<Tooltip
												title={`${comparisonLabel}: ${formatTick(item.compTkm.value)} ${item.compTkm.measure}`}
												arrow
											>
												<Stack
													position="absolute"
													left={0}
													top={barHeight + barGap}
													width={compWidth}
													height={barHeight}
													borderRadius="0 4px 4px 0"
													bgcolor="secondary.main"
												/>
											</Tooltip>
										</Stack>
									</Stack>
								</Stack>
							);
						})}
				</Stack>

				{/* X axis labels */}
				<Stack direction="row" spacing={1} alignItems="center">
					<Stack width={axisWidth} />

					<Stack flex={1} px={chartPaddingX}>
						<Stack position="relative" height={22}>
							{ticks.map((tick, index) => {
								const left = `${(tick / axisMaxValue) * 100}%`;

								return (
									<Stack
										key={`tick-${tick}-${index}`}
										position="absolute"
										left={left}
										sx={{ transform: "translateX(-50%)" }}
									>
										<Typography variant="caption" color="text.secondary" whiteSpace="nowrap">
											{formatTick(tick)}
										</Typography>
									</Stack>
								);
							})}
						</Stack>
					</Stack>
				</Stack>

				{/* Legend */}
				{!hasNoData && (
					<GraphBarChartLegend
						title={measure}
						primaryLable={baselineLabel}
						secondaryLable={comparisonLabel}
						primaryColor="primary.main"
						secondaryColor="secondary.main"
					/>
				)}
			</Stack>
		</Paper>
	);
};

export default ModalShiftBarChart;
